<script setup lang="ts">
import {nextTick, onMounted, ref, watch} from 'vue';
import {useRouter} from 'vue-router';
import ZZCMark from '@/views/header/ZZCMark.vue';
import TextButton from '@/views/ui/TextButton.vue';
import StatusTag from '@/views/ui/StatusTag.vue';
import {getAppVersion} from '@/base/frontendConfig';
import {clearMe, getMePermission} from '@/base/me.js';
import {login, loginCode, start, wachtwoordVergeten} from '@/base/auth.js';
import {landingVan, LoginAntwoord, StartAntwoord} from 'common';

type ReportState = { text: string, error: boolean };

// email: only the address; wachtwoord: the password of a known address; code: the mailed login code;
// mail: a set-password mail went out, nothing to type here
// kantoor: staff outside the office network, nothing to type here
type Stap = 'email' | 'wachtwoord' | 'code' | 'mail' | 'kantoor';

const router      = useRouter();
const email       = ref('');
const password    = ref('');
const stap        = ref<Stap>('email');
const emailField      = ref<HTMLInputElement | null>(null);
const passwordField   = ref<HTMLInputElement | null>(null);
const codeField       = ref<HTMLInputElement | null>(null);
const heeftWachtwoord = ref<boolean>(true);

// every step has one field to type in; it gets the focus as soon as the step renders, so Enter flows on
watch(stap, async () => {
    await nextTick();
    const veld: HTMLInputElement | null = stap.value === 'wachtwoord' ? passwordField.value : stap.value === 'code' ? codeField.value : emailField.value;
    veld?.focus();
});
const code        = ref('');
const onthoud     = ref(false);
const reportState = ref<ReportState>({
    text : '',
    error: false,
});

let errorTimerId: number | ReturnType<typeof setTimeout>;

onMounted(async () => {
    await permissionRedirect();
});

async function onStart() {
    if (email.value.trim() === '') {
        setReportState(makeError('vul je e-mail in'));
        return;
    }
    const antwoord: StartAntwoord = await start(email.value);
    if (antwoord.status === 'mail') {
        stap.value = 'mail';
    } else if (antwoord.status === 'code') {
        heeftWachtwoord.value = false;
        stap.value            = 'code';
        setReportState(makeOk('code verstuurd'));
    } else if (antwoord.status === 'kantoor') {
        stap.value = 'kantoor';
    } else if (antwoord.status === 'geblokkeerd') {
        setReportState(makeError('te veel pogingen, wacht even'));
    } else {
        heeftWachtwoord.value = true;
        stap.value            = 'wachtwoord';
    }
}

function onEnter() {
    switch (stap.value) {
        case 'email':
            onStart();
            break;
        case 'wachtwoord':
            onLogin();
            break;
        case 'code':
            onCode();
            break;
        case 'mail':
        case 'kantoor':
            break;
    }
}

async function onLogin() {
    clearMe();
    const antwoord: LoginAntwoord = await login(email.value, password.value);
    if (antwoord.status === 'ok') {
        await permissionRedirect();
    } else if (antwoord.status === 'code') {
        stap.value = 'code';
        setReportState(makeOk('code verstuurd'));
    } else if (antwoord.status === 'geblokkeerd') {
        setReportState(makeError('te veel pogingen, wacht even'));
    } else {
        setReportState(makeError('e-mail/wachtwoord fout'));
    }
}

async function onCode() {
    clearMe();
    const antwoord: LoginAntwoord = await loginCode(email.value, code.value, onthoud.value);
    if (antwoord.status === 'ok') {
        await permissionRedirect();
    } else if (antwoord.status === 'geblokkeerd') {
        setReportState(makeError('te veel pogingen, wacht even'));
    } else if ((antwoord.pogingenOver ?? 0) > 0) {
        setReportState(makeError(`code fout, nog ${antwoord.pogingenOver} pogingen`));
    } else {
        stap.value = heeftWachtwoord.value ? 'wachtwoord' : 'email';
        setReportState(makeError('code verlopen, log opnieuw in'));
    }
}

async function onPwReset() {
    if (email.value.trim() === '') {
        setReportState(makeError('vul je e-mail in'));
        return;
    }
    await wachtwoordVergeten(email.value);
    setReportState(makeOk('als het adres bekend is, is er een mail gestuurd'));
}

function makeError(text: string): ReportState {
    return {
        text,
        error: true,
    };
}

function makeOk(text?: string): ReportState {
    return {
        text : text ?? '',
        error: false,
    };
}

function setReportState(state: ReportState) {
    clrReportState();
    if (state.text) {
        reportState.value = state;
        if (errorTimerId) {
            clearTimeout(errorTimerId);
        }
        errorTimerId = setTimeout(clrReportState, 5_000);
    }
    return !state.error;
}

function clrReportState() {
    reportState.value = makeOk();
    if (errorTimerId) {
        clearTimeout(errorTimerId);
    }
}

async function permissionRedirect() {
    const permissions = await getMePermission();
    if (!permissions) {
        console.log(`you need to login first`);
    } else {
        const pad: string | undefined = landingVan(permissions);
        if (pad) {
            await router.push(pad);
        } else {
            console.error(`permission config problem: you only have permissions: ${JSON.stringify(permissions)}`);
        }
    }
}

</script>

<template>
    <div class="min-h-screen bg-zzc-lichtgrijs flex items-center justify-center p-6">
        <div
            @keydown.enter="onEnter"
            class="w-[360px] bg-white rounded-card border border-zzc-rand shadow-card p-7 flex flex-col gap-4"
        >
            <div class="flex items-center gap-3">
                <ZZCMark :size="36"/>
                <div class="flex flex-col leading-tight">
                    <span class="font-semibold text-zzc-blauw">Zeeuwse Zorgcoalitie</span>
                    <span class="text-[11px] text-zzc-muted">{{ getAppVersion() }}</span>
                </div>
            </div>
            <div class="text-lg font-semibold text-zzc-blauw">Rapportage Login</div>
            <div v-if="stap === 'email' || stap === 'wachtwoord'">
                <label
                    for="email"
                    class="block text-xs font-medium text-zzc-muted cursor-pointer"
                >
                    e-mail
                </label>
                <input
                    id="email"
                    ref="emailField"
                    v-model="email"
                    type="text"
                    :disabled="stap === 'wachtwoord'"
                    data-testid="login-email"
                    class="mt-1 px-3 py-2 block w-full rounded-control border border-zzc-rand focus:border-zzc-blauw focus:outline-none focus:ring-2 focus:ring-[var(--el-color-primary-light-8)] disabled:text-zzc-muted disabled:bg-zzc-lichtgrijs"
                />
            </div>
            <div v-if="stap === 'mail'" class="text-sm text-zzc-muted" data-testid="login-mail-gestuurd">
                Er is een mail gestuurd naar <b>{{ email }}</b> met een link om je wachtwoord te zetten.
                Log daarna hier in. Na het inloggen met je wachtwoord ontvang je voortaan een inlogcode per mail.
            </div>
            <div v-if="stap === 'kantoor'" class="text-sm text-zzc-muted" data-testid="login-kantoor">
                Inloggen met <b>{{ email }}</b> kan alleen vanaf kantoor of via de VPN.
            </div>
            <div v-if="stap === 'wachtwoord'">
                <label
                    for="password"
                    class="block text-xs font-medium text-zzc-muted cursor-pointer"
                >
                    wachtwoord
                </label>
                <input
                    id="password"
                    ref="passwordField"
                    v-model="password"
                    type="password"
                    data-testid="login-password"
                    class="mt-1 px-3 py-2 block w-full rounded-control border border-zzc-rand focus:border-zzc-blauw focus:outline-none focus:ring-2 focus:ring-[var(--el-color-primary-light-8)] disabled:text-zzc-muted disabled:bg-zzc-lichtgrijs"
                />
            </div>
            <div v-if="stap !== 'mail' && stap !== 'kantoor'" class="flex flex-row justify-between items-center w-full">
                <Transition
                    enter-from-class="opacity-0"
                    enter-active-class="transition-opacity duration-300 ease-out"
                    enter-to-class="opacity-100"
                    leave-from-class="opacity-100"
                    leave-active-class="transition-opacity duration-1000 ease-in"
                    leave-to-class="opacity-0"
                >
                    <StatusTag v-if="reportState.text" :kind="reportState.error ? 'bad' : 'ok'">
                        {{ reportState.text }}
                    </StatusTag>
                </Transition>
                <div v-if="!reportState.text" class="py-1 px-2">
                </div>
                <TextButton
                    v-if="stap === 'wachtwoord'"
                    variant="text"
                    @click="onPwReset"
                >
                    Wachtwoord&nbsp;reset
                </TextButton>
            </div>
            <div v-if="stap === 'email'" class="flex items-center">
                <TextButton
                    variant="primary"
                    class="w-full justify-center"
                    data-testid="login-verder-button"
                    @click="onStart"
                >
                    Verder
                </TextButton>
            </div>
            <template v-else-if="stap === 'wachtwoord'">
                <div class="flex items-center">
                    <TextButton
                        variant="primary"
                        class="w-full justify-center"
                        data-testid="login-button"
                        @click="onLogin"
                    >
                        Inloggen
                    </TextButton>
                </div>
                <div class="text-center">
                    <button
                        @click="stap = 'email'"
                        class="text-xs text-zzc-blauw hover:underline cursor-pointer"
                    >
                        ander e-mailadres
                    </button>
                </div>
            </template>
            <div v-else-if="stap === 'mail' || stap === 'kantoor'" class="text-center">
                <button
                    @click="stap = 'email'"
                    class="text-xs text-zzc-blauw hover:underline cursor-pointer"
                >
                    terug
                </button>
            </div>
            <template v-else>
                <div class="text-sm text-zzc-muted">
                    We hebben een code gestuurd naar {{ email }}
                </div>
                <div>
                    <label
                        for="code"
                        class="block text-xs font-medium text-zzc-muted cursor-pointer"
                    >
                        code
                    </label>
                    <input
                        id="code"
                        ref="codeField"
                        v-model="code"
                        type="text"
                        inputmode="numeric"
                        autocomplete="one-time-code"
                        data-testid="login-code"
                        class="mt-1 px-3 py-2 block w-full rounded-control border border-zzc-rand focus:border-zzc-blauw focus:outline-none focus:ring-2 focus:ring-[var(--el-color-primary-light-8)] disabled:text-zzc-muted disabled:bg-zzc-lichtgrijs"
                    />
                </div>
                <div v-if="heeftWachtwoord" class="flex items-center gap-2">
                    <input
                        id="onthoud"
                        v-model="onthoud"
                        type="checkbox"
                        data-testid="login-onthoud"
                        class="cursor-pointer"
                    />
                    <label for="onthoud" class="text-sm text-zzc-muted cursor-pointer">
                        Deze browser 30 dagen vertrouwen
                    </label>
                </div>
                <div class="flex items-center">
                    <TextButton
                        variant="primary"
                        class="w-full justify-center"
                        data-testid="login-code-button"
                        @click="onCode"
                    >
                        Bevestigen
                    </TextButton>
                </div>
                <div class="text-center">
                    <button
                        @click="stap = heeftWachtwoord ? 'wachtwoord' : 'email'"
                        class="text-xs text-zzc-blauw hover:underline cursor-pointer"
                    >
                        terug
                    </button>
                </div>
            </template>
        </div>
    </div>
</template>
